import { isEmpty } from 'lodash';
import { FormattedHTMLMessage, FormattedMessage } from 'react-intl';
import {
  formatDuration,
  formatRoundedDuration,
  getRoundPrice,
} from '../../../helpers/results';
import {
  GENERAL_TEXT_SENTENCES_1,
  GENERAL_TEXT_SENTENCES_2,
  GENERAL_TEXT_SENTENCES_2_DIRECT,
  GENERAL_TEXT_SENTENCES_3,
  SEARCH_PAGE_TYPE_TO_TRANSPORT_TYPE,
  TRANSPORT_MEANS,
} from '../constants';
import messages from '../View/messages';
import { TextGen } from './TextGen';

/**
 * @description TextGen class that formats the general text to describe the trip (first accordion).
 * General text contains information such as companies and distance, and is largely used to induce variations between search pages.
 * It does not contain specific information about the trip such as price, duration, stops etc... (cf SpecificTextGen)
 */
class GeneralTextGen extends TextGen {
  constructor(textGen) {
    super(textGen);
    this.intl = textGen.intl;
    this.chance = textGen.chance;
    this.seed = textGen.seed;
  }

  /**
   * @description Main function formatting the general text block with one header and a content body composed of 3 sentences.
   * @param {object} state - State data
   * @returns {{header: FormattedMessage, content: *}|null}
   */
  formatGeneralTextData(state, dynamicInformation) {
    if (isEmpty(state.tripData)) {
      return null;
    }
    const origin = state.originCity.name;
    const destination = state.destinationCity.name;
    const formattedTrips = this.getFormattedTrips(origin, destination);
    const transportModes = Array.from({ length: 5 }, (v, i) =>
      this.intl.formatMessage(
        messages[TRANSPORT_MEANS[(this.seed + i) % TRANSPORT_MEANS.length]],
      ),
    );
    const pageTransportType =
      SEARCH_PAGE_TYPE_TO_TRANSPORT_TYPE[state.currentPageType];

    const transportTypeMessage = this.intl.formatMessage(
      messages[pageTransportType ?? 'genericTransportType'],
    );
    const sentence1 = this.formatSentence1(
      origin,
      destination,
      formattedTrips,
      transportModes,
      state.tripData,
      transportTypeMessage,
      dynamicInformation,
    );

    const sentence2 = this.formatSentence2(
      origin,
      destination,
      formattedTrips,
      transportModes,
      state.tripData.hasDirectTrip,
      transportTypeMessage,
    );

    let sentence3 = '';
    if (
      dynamicInformation &&
      dynamicInformation.price &&
      dynamicInformation.duration
    ) {
      sentence3 = this.formatSentence3(
        origin,
        destination,
        formattedTrips,
        getRoundPrice(dynamicInformation.price.min),
        formatDuration(dynamicInformation.duration.min),
        transportTypeMessage,
      );
    }

    return {
      header: (
        <FormattedMessage
          {...messages[
            pageTransportType
              ? 'generalSpecificTextHeader'
              : 'generalTextHeader'
          ]}
          values={{
            origin: state.originCity.name,
            destination: state.destinationCity.name,
            transportType: transportTypeMessage,
          }}
        />
      ),
      content: (
        <div>
          <p>{sentence1}</p>
          <p>{sentence2}</p>
          <p>{sentence3}</p>
        </div>
      ),
    };
  }

  /**
   * @description Formats the first sentence, choosing between multiple possible sentences
   * @param {string} origin - Name of the origin city
   * @param {string} destination - Name of the destination city
   * @param {string[]} formattedTrips - Multiple choices of displaying the trip (induces variations)
   * @param {string[]} transportModes - Multiple choices of transport mans (induces variations)
   * @param {object} tripData - Trip data returned by api
   * @returns {FormattedHTMLMessage} - First sentence
   */
  formatSentence1(
    origin,
    destination,
    formattedTrips,
    transportModes,
    tripData,
    transportTypeMessage,
    dynamicInformation,
  ) {
    return (
      <FormattedHTMLMessage
        {...messages[
          GENERAL_TEXT_SENTENCES_1[this.seed % GENERAL_TEXT_SENTENCES_1.length]
        ]}
        values={{
          origin,
          destination,
          formattedTrip1: formattedTrips[0],
          formattedTrip2: formattedTrips[1],
          transportMode1: transportModes[0],
          transportMode2: transportModes[1],
          companies: this.formatCompanies(dynamicInformation?.companies || []),
          distance: Math.round(tripData.distanceAsCrowFlies),
          minDuration: formatRoundedDuration(
            dynamicInformation?.minDuration || 0,
          ),
          transportType: transportTypeMessage,
        }}
      />
    );
  }

  /**
   * @description Formats the second sentence depending on whether the trip is direct or not
   * @param {string} origin - Name of the origin city
   * @param {string} destination - Name of the destination city
   * @param {string[]} formattedTrips - Multiple choices of displaying the trip (induces variations)
   * @param {string[]} transportModes - Multiple choices of transport mans (induces variations)
   * @param {boolean} direct - Indicates if the trip is direct (no stops)
   * @returns {FormattedHTMLMessage} - Second sentence
   */
  formatSentence2(
    origin,
    destination,
    formattedTrips,
    transportModes,
    direct,
    transportTypeMessage,
  ) {
    if (direct) {
      return (
        <FormattedHTMLMessage
          {...messages[
            GENERAL_TEXT_SENTENCES_2_DIRECT[
              this.seed % GENERAL_TEXT_SENTENCES_2_DIRECT.length
            ]
          ]}
          values={{
            origin,
            destination,
            formattedTrip: formattedTrips[2],
            transportType: transportTypeMessage,
          }}
        />
      );
    }

    return (
      <FormattedHTMLMessage
        {...messages[
          GENERAL_TEXT_SENTENCES_2[this.seed % GENERAL_TEXT_SENTENCES_2.length]
        ]}
        values={{
          origin,
          destination,
          formattedTrip1: formattedTrips[2],
          formattedTrip2: formattedTrips[3],
          transportMode1: transportModes[2],
          transportMode2: transportModes[3],
          transportType: transportTypeMessage,
        }}
      />
    );
  }

  /**
   * @description Formats the third sentence
   * @param {string[]} formattedTrips - Multiple choices of displaying the trip (induces variations)
   * @param {number} minPrice - Minimum price for the trip in euros (rounded)
   * @param {string} minDuration - Minimum duration for the trip (ex: "2h10")
   * @returns {FormattedHTMLMessage} - Third sentence
   */
  formatSentence3(
    origin,
    destination,
    formattedTrips,
    minPrice,
    minDuration,
    transportTypeMessage,
  ) {
    return (
      <FormattedHTMLMessage
        {...messages[
          GENERAL_TEXT_SENTENCES_3[this.seed % GENERAL_TEXT_SENTENCES_3.length]
        ]}
        values={{
          origin,
          destination,
          formattedTrip1: formattedTrips[4],
          formattedTrip2: formattedTrips[5],
          minPrice,
          minDuration,
          only: minPrice < 20 ? this.intl.formatMessage(messages.only) : '',
          transportType: transportTypeMessage,
        }}
      />
    );
  }

  /**
   * @description Formats the companies into a sentence adding the transport means and linking words ("of", "and")
   * @param {{transportType: string, fullname: string}[]} companies - Companies involved in a trip (returned by api)
   * @returns {string} - Sentence enumerating company names with their corresponding transport means
   */
  formatCompanies = (companies) => {
    const formattedCompanies = companies.map(
      (company) =>
        `${this.intl.formatMessage(messages[company.transportType])} ${
          company.fullname
        }`,
    );

    return this.formatEnumeratingSentence(formattedCompanies, ', ');
  };

  /**
   * @description Retrieves the minimum price and duration of a trip comparing all transport means
   * @param {object} transportInformation - Api response containing info about the trip
   * @returns {{minPrice: number, minDuration: number}} - Min price in cents and Min Duration in seconds
   */
  getMinPriceDuration = (transportInformation) =>
    Object.values(transportInformation).reduce(
      (acc, data) => {
        if (!isEmpty(data)) {
          const minPrice = data.bestItinerary.price.min;
          const minDuration = data.bestItinerary.duration.min;
          acc.minPrice = minPrice < acc.minPrice ? minPrice : acc.minPrice;
          acc.minDuration =
            minDuration < acc.minDuration ? minDuration : acc.minDuration;
        }

        return acc;
      },
      {
        minPrice: Number.MAX_SAFE_INTEGER,
        minDuration: Number.MAX_SAFE_INTEGER,
      },
    );
}

export { GeneralTextGen };
